import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, addWithConfig } from '@rbilabs/universal-components';
import { Image, ImageBackground } from 'expo-image';
import LottieView from 'lottie-react-native';
import { usePropsResolution, useStyledSystemPropsResolver } from 'native-base';
import { ImageStyle, LayoutChangeEvent, Platform, useWindowDimensions } from 'react-native';

import { buildImageUrl, parseAssetID } from 'remote/build-image-url';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { isWeb } from 'utils/environment';
import {
  PictureBoundaryDimensions,
  generateSrcUri,
  getDefaultQuality,
  getImageFormat,
} from 'utils/image';
import { useLottieSource } from 'utils/lottie/use-lottie-source';

import { IPictureProps, MeasuredDimensions } from './types';
import { getBoundaryDimensions, isSVG } from './utils';

export { FullWidthAutoHeightImage } from './full-width-auto-height-image';

const defaultFallbackImage = require('../../assets/images/fallback-image.png');

const Boundary = Box.withConfig({
  overflow: 'hidden',
  width: 'full',
});

const imageStyle: ImageStyle = {
  width: '100%',
  height: '100%',
};

const imagePropsSizedByChildren: ImageStyle = {
  width: 'auto',
  height: 'auto',
};

// used conditionally if device supports avif
const avifHeaders = { accept: 'image/avif,image/webp,image/*' };

// A react native extension file for the picture component has been created
// since react-native-web does not currently support source sets properly for web
// through the RN Image component.
// Reference: https://github.com/necolas/react-native-web/issues/515
const Picture = ({
  alt,
  children,
  image,
  lottie,
  objectFitContain = false,
  quality,
  resolutionMultiplier = 1, // Not sure if this is necessary anymore. Should we remove it?
  onImageLoaded,
  sizedByChildren = false,
  cache = 'force-cache', // per sanity if an image changes its UUID will always change so this is a sensible default
  isBlurhashEnabled = true,
  onLayout,
  priority,
  fallbackImage,
  onDisplay,
  ...boundaryBoxProps
}: IPictureProps) => {
  const { width: screenWidth } = useWindowDimensions();
  const metadata = image?.asset?.metadata;
  // blurhash doesn't seem to work right on web and creates a glitch loading so only use on native
  const blurHash =
    isBlurhashEnabled && Platform.OS !== 'web' ? (metadata as any)?.blurHash : undefined;
  const placeholder = blurHash ? { blurhash: blurHash } : null;
  const [hasError, setHasError] = useState(false);
  const [resolvedBoundaryBoxStyleProps] = useStyledSystemPropsResolver(
    usePropsResolution('Picture', boundaryBoxProps)
  );

  const [measuredDimensions, setMeasuredDimensions] = useState<MeasuredDimensions>({
    width: 0,
    height: 0,
  });

  const { format, dimensions } = parseAssetID(image?.asset?._id || undefined);
  const isFinalFormatSvg = isSVG(format);

  const boundaryDimensions: PictureBoundaryDimensions = useMemo(() => {
    return sizedByChildren
      ? { height: null, width: null }
      : getBoundaryDimensions({
          extrinsicDimensions: {
            width: resolvedBoundaryBoxStyleProps.width,
            height: resolvedBoundaryBoxStyleProps.height,
          },
          intrinsicDimensions: dimensions,
          measuredDimensions,
        });
  }, [
    dimensions,
    measuredDimensions,
    resolvedBoundaryBoxStyleProps.height,
    resolvedBoundaryBoxStyleProps.width,
    sizedByChildren,
  ]);

  const prevUri = useRef('');
  const prevImage = useRef('');
  const imageFormat = getImageFormat(image);
  const sanityProjectId = useFlag(LaunchDarklyFlag.SANITY_PROJECT_ID);
  quality = quality ? quality : getDefaultQuality(imageFormat);

  const resolvedURI = useMemo(() => {
    if (JSON.stringify(prevImage.current) !== JSON.stringify(image)) {
      prevUri.current = '';
      prevImage.current = JSON.stringify(image);
    }

    if (!image?.asset?._id) {
      return '';
    }

    // if this image has a size specified (not just 100%) ignore handleLayoutEvent changes that may change the requested URI's size
    // leading to duplicate requests and longer image loading time
    // TODO - find a better way to implement this component / big refactor of it
    if (
      boundaryDimensions.height &&
      boundaryDimensions.width &&
      (boundaryDimensions.height !== '100%' || boundaryDimensions.width !== '100%') &&
      prevUri.current
    ) {
      return prevUri.current;
    }

    const uri = isFinalFormatSvg
      ? buildImageUrl(image, sanityProjectId)
      : generateSrcUri({
          image,
          boundaryDimensions,
          measuredDimensions,
          screenWidth,
          quality,
          imageFormat,
          resolutionMultiplier,
          sanityProjectId,
        });

    prevUri.current = uri;

    return uri;
  }, [
    boundaryDimensions,
    image,
    isFinalFormatSvg,
    measuredDimensions,
    quality,
    resolutionMultiplier,
    screenWidth,
    imageFormat,
    sanityProjectId,
  ]);

  const handleLayoutEvent = useCallback(
    (event: LayoutChangeEvent) => {
      const layout = event.nativeEvent.layout;
      if (
        layout.width !== measuredDimensions.width ||
        layout.height !== measuredDimensions.height
      ) {
        setMeasuredDimensions({
          width: event.nativeEvent.layout.width,
          height: event.nativeEvent.layout.height,
        });
        onLayout?.(event);
      }
    },
    [measuredDimensions.height, measuredDimensions.width, onLayout]
  );

  const wrappedOnLoad = useCallback(() => {
    onImageLoaded?.();
  }, [onImageLoaded]);

  const lottieUrl = lottie?.asset?.url;
  const { source: preparedLottieSource, error: lottiePreparationError } = useLottieSource({
    source: lottieUrl ? { uri: lottieUrl } : undefined,
    surface: 'picture',
  });

  useEffect(() => {
    setHasError(false);
  }, [lottieUrl]);

  const source = useMemo(() => {
    if (!image?.asset?._id && !lottieUrl) {
      return null;
    }

    if (lottieUrl) {
      return {
        uri: lottieUrl,
      };
    }

    return {
      uri: resolvedURI,
      headers: imageFormat === 'avif' ? avifHeaders : undefined,
    };
  }, [image?.asset?._id, lottieUrl, resolvedURI, imageFormat]);

  if (!image?.asset && !lottieUrl) {
    return <>{children}</>;
  }

  if (!source) {
    return null;
  }

  const selectedFallbackImage = fallbackImage ?? defaultFallbackImage;
  // TODO we are currently missing a ton of alt images (just look at all the console warnings on web), sub this in for now if missing
  const accessibilityLabel = alt || '';

  const finalImageStyle = sizedByChildren ? imagePropsSizedByChildren : imageStyle;

  const ImageComponent = children ? ImageBackground : Image;

  const handleError = () => {
    setHasError(true);
  };

  return (
    <Boundary
      width={boundaryDimensions.width}
      height={boundaryDimensions.height}
      onLayout={handleLayoutEvent}
      {...(lottie ? { 'aria-label': accessibilityLabel } : {})}
      {...boundaryBoxProps}
    >
      {lottieUrl && preparedLottieSource && !hasError && (
        <LottieView
          style={finalImageStyle}
          source={preparedLottieSource}
          autoPlay
          loop
          onAnimationLoaded={wrappedOnLoad}
          onAnimationFailure={handleError}
        />
      )}
      {lottieUrl && (lottiePreparationError || hasError) && (
        <ImageBackground
          source={selectedFallbackImage}
          style={finalImageStyle}
          onLoad={wrappedOnLoad}
        />
      )}
      {!lottieUrl && (
        <>
          {hasError && <ImageBackground source={selectedFallbackImage} style={finalImageStyle} />}
          <ImageComponent
            {...(isWeb ? { testid: 'picture-img' } : { testID: 'picture-img' })} // for web we need to use testid instead of testID
            placeholder={placeholder}
            placeholderContentFit="cover"
            source={source}
            style={finalImageStyle}
            onLoad={wrappedOnLoad}
            contentFit={objectFitContain ? 'contain' : 'cover'}
            onError={handleError}
            onDisplay={onDisplay}
            priority={priority}
            {...((boundaryBoxProps?.importantForAccessibility === 'no' && {
              accessibilityLabel: '',
              importantForAccessibility: 'no',
            }) || {
              accessibilityLabel,
            })}
            children={children ? <Boundary>{children}</Boundary> : undefined} // if background image
          />
        </>
      )}
    </Boundary>
  );
};
export default addWithConfig(React.memo(Picture));
